// ===== Shared types/parsing/helpers for raw 276/277 (segment-based) claim status data =====

export interface Raw277ClaimStatus {
  patientControlNumber?: string;
  payerClaimControlNumber?: string;
  billTypeCode?: string;
  traceNumber?: string;
  serviceDateFrom?: string;
  serviceDateTo?: string;
  chargeAmount?: number;
  paidAmount?: number;
  subscriberName?: string;
  subscriberId?: string;
  dependentName?: string;
  providerName?: string;
  providerNpi?: string;
  payerName?: string;
  payerId?: string;
  receiverName?: string;
  statusInfos: Array<{
    categoryCode?: string;
    statusCode?: string;
    entityCode?: string;
    date?: string;
    chargeAmount?: number;
    paidAmount?: number;
  }>;
  serviceLines: Array<{
    procedureCode?: string;
    chargeAmount?: number;
    paidAmount?: number;
    units?: number;
    serviceDate?: string;
    statusInfos: Array<{
      categoryCode?: string;
      statusCode?: string;
      entityCode?: string;
      date?: string;
    }>;
  }>;
}

// Status code category descriptions
const STATUS_CATEGORIES: Record<string, string> = {
  A0: "Acknowledgement/Forwarded",
  A1: "Acknowledgement/Receipt",
  A2: "Acknowledgement/Acceptance",
  A3: "Acknowledgement/Returned as Unprocessable",
  A4: "Acknowledgement/Not Found",
  A5: "Acknowledgement/Split Claim",
  A6: "Acknowledgement/Rejected",
  A7: "Acknowledgement/Hold/Pending",
  A8: "Acknowledgement/Pend/Forwarded",
  E0: "Pending/Response/Claim",
  E1: "Pending/Response/Line",
  E2: "Pending/Response/Info",
  F0: "Finalized/Payment",
  F1: "Finalized/Denial",
  F2: "Finalized/Partial Payment",
  F3: "Finalized/Adjudication Complete",
  F4: "Finalized/Reversed",
  P0: "Pending/Adjudication",
  P1: "Pending/Review",
  P2: "Pending/Missing Info",
  P3: "Pending/Authorization",
  P4: "Pending/Coordination",
  R0: "Requests for Additional Info",
  R1: "Requests for Medical Records",
  R3: "Requests for Documentation",
  R4: "Requests for Clinical Info",
  R5: "Requests for Resubmission",
};

export const getCategoryDescription = (code?: string): string => {
  if (!code) return "Unknown";
  return STATUS_CATEGORIES[code] || code;
};

export const getCategoryColor = (code?: string): string => {
  if (!code) return "bg-gray-100 text-gray-800";
  const prefix = code[0];
  switch (prefix) {
    case "A":
      return "bg-blue-100 text-blue-800";
    case "E":
      return "bg-yellow-100 text-yellow-800";
    case "F":
      return code === "F0"
        ? "bg-green-100 text-green-800"
        : code === "F1"
          ? "bg-red-100 text-red-800"
          : "bg-orange-100 text-orange-800";
    case "P":
      return "bg-purple-100 text-purple-800";
    case "R":
      return "bg-cyan-100 text-cyan-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const processClaimTracking = (
  tracking: {
    current_transaction_trace_number?: string;
    claim_level_status_information?: Array<{
      health_care_claim_status?: {
        health_care_claim_status_category_code?: string;
        status_code?: string;
        entity_identifier_code?: { code?: string };
      };
      date?: string;
      total_submitted_charges_for_unit_work?: number;
      monetary_amount?: number;
    }>;
    payer_claim_control_number?: { member_group_or_policy_number?: string };
    patient_control_number?: { member_group_or_policy_number?: string };
    institutional_bill_type_identification?: {
      member_group_or_policy_number?: string;
    };
    claim_service_date?: {
      date_time_period?: string;
      date_time_period_to?: string;
    };
    service_line_information?: Array<{
      composite_medical_procedure_identifier?: { procedure_code?: string };
      line_item_charge_amount?: number;
      monetary_amount?: number;
      units_of_service_count?: number;
      service_line_date?: { date_time_period?: string };
      service_line_status_information?: Array<{
        health_care_claim_status?: {
          health_care_claim_status_category_code?: string;
          status_code?: string;
          entity_identifier_code?: { code?: string };
        };
        date?: string;
      }>;
    }>;
  },
  context: {
    payerName?: string;
    payerId?: string;
    receiverName?: string;
    providerName?: string;
    providerNpi?: string;
    subscriberName?: string;
    subscriberId?: string;
    dependentName?: string;
  },
): Raw277ClaimStatus => {
  const statusInfos = (tracking.claim_level_status_information || []).map(
    (stc) => ({
      categoryCode:
        stc.health_care_claim_status?.health_care_claim_status_category_code,
      statusCode: stc.health_care_claim_status?.status_code,
      entityCode: stc.health_care_claim_status?.entity_identifier_code?.code,
      date: stc.date,
      chargeAmount: stc.total_submitted_charges_for_unit_work,
      paidAmount: stc.monetary_amount,
    }),
  );

  const serviceLines = (tracking.service_line_information || []).map((svc) => ({
    procedureCode: svc.composite_medical_procedure_identifier?.procedure_code,
    chargeAmount: svc.line_item_charge_amount,
    paidAmount: svc.monetary_amount,
    units: svc.units_of_service_count,
    serviceDate: svc.service_line_date?.date_time_period,
    statusInfos: (svc.service_line_status_information || []).map((lineStc) => ({
      categoryCode:
        lineStc.health_care_claim_status
          ?.health_care_claim_status_category_code,
      statusCode: lineStc.health_care_claim_status?.status_code,
      entityCode:
        lineStc.health_care_claim_status?.entity_identifier_code?.code,
      date: lineStc.date,
    })),
  }));

  return {
    traceNumber: tracking.current_transaction_trace_number,
    patientControlNumber:
      tracking.patient_control_number?.member_group_or_policy_number,
    payerClaimControlNumber:
      tracking.payer_claim_control_number?.member_group_or_policy_number,
    billTypeCode:
      tracking.institutional_bill_type_identification
        ?.member_group_or_policy_number,
    serviceDateFrom: tracking.claim_service_date?.date_time_period,
    serviceDateTo: tracking.claim_service_date?.date_time_period_to,
    chargeAmount: statusInfos[0]?.chargeAmount,
    paidAmount: statusInfos[0]?.paidAmount,
    ...context,
    statusInfos,
    serviceLines,
  };
};

export const parseRaw277Data = (data: unknown[]): Raw277ClaimStatus[] => {
  const claims: Raw277ClaimStatus[] = [];

  // Find ST segment (transaction set header)
  const stSegment = data.find(
    (seg: unknown) =>
      (seg as { segment_id?: string }).segment_id === "ST" &&
      (seg as { transaction_set_identifier_code?: { code?: string } })
        .transaction_set_identifier_code?.code === "277",
  ) as {
    information_source_level?: Array<{
      payer_name?: {
        response_contact_last_or_organization_name?: string;
        response_contact_identifier?: string;
      };
      information_receiver_level?: Array<{
        information_receiver_name?: {
          response_contact_last_or_organization_name?: string;
          response_contact_identifier?: string;
        };
        service_provider_level?: Array<{
          provider_name?: Array<{
            response_contact_last_or_organization_name?: string;
            response_contact_identifier?: string;
          }>;
          subscriber_level?: Array<{
            subscriber_name?: {
              response_contact_last_or_organization_name?: string;
              response_contact_first_name?: string;
              response_contact_identifier?: string;
            };
            claim_status_tracking_number?: Array<{
              current_transaction_trace_number?: string;
              claim_level_status_information?: Array<{
                health_care_claim_status?: {
                  health_care_claim_status_category_code?: string;
                  status_code?: string;
                  entity_identifier_code?: { code?: string };
                };
                date?: string;
                total_submitted_charges_for_unit_work?: number;
                monetary_amount?: number;
              }>;
              payer_claim_control_number?: {
                member_group_or_policy_number?: string;
              };
              patient_control_number?: {
                member_group_or_policy_number?: string;
              };
              institutional_bill_type_identification?: {
                member_group_or_policy_number?: string;
              };
              claim_service_date?: {
                date_time_period?: string;
                date_time_period_to?: string;
              };
              service_line_information?: Array<{
                composite_medical_procedure_identifier?: {
                  procedure_code?: string;
                };
                line_item_charge_amount?: number;
                monetary_amount?: number;
                units_of_service_count?: number;
                service_line_date?: { date_time_period?: string };
                service_line_status_information?: Array<{
                  health_care_claim_status?: {
                    health_care_claim_status_category_code?: string;
                    status_code?: string;
                    entity_identifier_code?: { code?: string };
                  };
                  date?: string;
                }>;
              }>;
            }>;
            dependent_level?: Array<{
              dependent_name?: {
                response_contact_last_or_organization_name?: string;
                response_contact_first_name?: string;
              };
              claim_status_tracking_number?: Array<{
                current_transaction_trace_number?: string;
                claim_level_status_information?: Array<{
                  health_care_claim_status?: {
                    health_care_claim_status_category_code?: string;
                    status_code?: string;
                    entity_identifier_code?: { code?: string };
                  };
                  date?: string;
                  total_submitted_charges_for_unit_work?: number;
                  monetary_amount?: number;
                }>;
                payer_claim_control_number?: {
                  member_group_or_policy_number?: string;
                };
                patient_control_number?: {
                  member_group_or_policy_number?: string;
                };
                claim_service_date?: {
                  date_time_period?: string;
                  date_time_period_to?: string;
                };
                service_line_information?: Array<{
                  composite_medical_procedure_identifier?: {
                    procedure_code?: string;
                  };
                  line_item_charge_amount?: number;
                  monetary_amount?: number;
                  units_of_service_count?: number;
                  service_line_date?: { date_time_period?: string };
                  service_line_status_information?: Array<{
                    health_care_claim_status?: {
                      health_care_claim_status_category_code?: string;
                      status_code?: string;
                      entity_identifier_code?: { code?: string };
                    };
                    date?: string;
                  }>;
                }>;
              }>;
            }>;
          }>;
        }>;
      }>;
    }>;
  };

  if (!stSegment) return claims;

  // Navigate the hierarchical structure
  const infoSourceLevels = stSegment.information_source_level || [];

  for (const infoSource of infoSourceLevels) {
    const payerName =
      infoSource.payer_name?.response_contact_last_or_organization_name;
    const payerId = infoSource.payer_name?.response_contact_identifier;

    const infoReceiverLevels = infoSource.information_receiver_level || [];

    for (const infoReceiver of infoReceiverLevels) {
      const receiverName =
        infoReceiver.information_receiver_name
          ?.response_contact_last_or_organization_name;

      const providerLevels = infoReceiver.service_provider_level || [];

      for (const provider of providerLevels) {
        const providerNames = provider.provider_name || [];
        const providerName =
          providerNames[0]?.response_contact_last_or_organization_name;
        const providerNpi = providerNames[0]?.response_contact_identifier;

        const subscriberLevels = provider.subscriber_level || [];

        for (const subscriber of subscriberLevels) {
          const subName = subscriber.subscriber_name;
          const subscriberName = [
            subName?.response_contact_first_name,
            subName?.response_contact_last_or_organization_name,
          ]
            .filter(Boolean)
            .join(" ");
          const subscriberId = subName?.response_contact_identifier;

          // Process subscriber-level claims
          const trackingNumbers = subscriber.claim_status_tracking_number || [];
          for (const tracking of trackingNumbers) {
            const claim = processClaimTracking(tracking, {
              payerName,
              payerId,
              receiverName,
              providerName,
              providerNpi,
              subscriberName,
              subscriberId,
            });
            claims.push(claim);
          }

          // Process dependent-level claims
          const dependentLevels = subscriber.dependent_level || [];
          for (const dependent of dependentLevels) {
            const depName = dependent.dependent_name;
            const dependentName = [
              depName?.response_contact_first_name,
              depName?.response_contact_last_or_organization_name,
            ]
              .filter(Boolean)
              .join(" ");

            const depTrackingNumbers =
              dependent.claim_status_tracking_number || [];
            for (const tracking of depTrackingNumbers) {
              const claim = processClaimTracking(tracking, {
                payerName,
                payerId,
                receiverName,
                providerName,
                providerNpi,
                subscriberName,
                subscriberId,
                dependentName,
              });
              claims.push(claim);
            }
          }
        }
      }
    }
  }

  return claims;
};
